<template>
  <section v-if="componentReady" class="container-wrapper modifications-page">
    <div class="container">
      <BaseButton
        type="secondary"
        class="modifications-page__prev"
        @click.native="goBack"
      >
        {{ backButtonText }}
      </BaseButton>

      <h2>{{ title }}</h2>

      <div class="modifications-page__content">
        <CarInfo
          :image="carImage"
          :name="car.name"
          :modification="carModification"
          :fuelType="!!currentFilter.modification ? carFuelType : ''"
          :volumeType="!!currentFilter.modification ? carVolumeType : ''"
          @imageEnterLeave="onImageEnterLEave"
        />

        <div class="modifications-page__filters">
          <BaseSelect
            v-if="get(filters, 'modification.types.length')"
            v-model="currentFilter.modification"
            :placeholder="filters.modification.placeholder"
            :label="filters.modification.label"
            :items="filters.modification.types"
            class="modifications-page__filter"
          />
          <BaseSelect
            v-if="get(filters, 'year.types.length')"
            v-model="currentFilter.year"
            :placeholder="filters.year.placeholder"
            :label="filters.year.label"
            :items="filters.year.types"
            class="modifications-page__filter"
            :desabled="!currentFilter.modification || !!currentFilter.year"
          />
          <BaseSelect
            v-if="get(filters, 'driveType.types.length')"
            v-model="currentFilter.driveType"
            :placeholder="filters.driveType.placeholder"
            :label="filters.driveType.label"
            :items="filters.driveType.types"
            class="modifications-page__filter"
            :desabled="!currentFilter.year || !!currentFilter.driveType"
          />
          <BaseSelect
            v-if="get(filters, 'additionalOption.types.length')"
            v-model="currentFilter.additionalOption"
            :placeholder="filters.additionalOption.placeholder"
            :label="filters.additionalOption.label"
            :items="filters.additionalOption.types"
            class="modifications-page__filter"
            :desabled="!currentFilter.driveType || !!currentFilter.additionalOption"
          />

          <div class="modifications-page__clear_all-wrapper">
            <BaseButton type="secondary" @click.native="clearFilters">
              {{ clearText }}
            </BaseButton>

            <CollapseTransition>
              <div v-if="isAllSelectsChacked" key="buttonWrapper">
                <BaseButton
                  class="modifications-page__next"
                  @click.native="submitForm"
                >
                  {{ nextButton }}
                </BaseButton>
              </div>
            </CollapseTransition>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  // Step 3

  import { debounce, get } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'

  import CarInfo from '@/components/CarInfo'
  import { CollapseTransition } from 'vue2-transitions'
  import { DEBOUNCE_REQUESTS, DEFAULT_LANG } from '@/helpers/constant'

  export default {
    name: 'ModificationPage',

    components: {
      CarInfo,
      CollapseTransition
    },

    data () {
      return {
        title: '',
        subtitle: '',
        clearText: '',
        subtitleNotFound: '',
        backButtonText: '',
        nextButton: '',
        car: null,
        filters: null,
        modifications: [],
        carModification: {
          year: null,
          driveType: null,
          additionalOption: null,
          modification: null
        },
        componentReady: false,
        currentFilter: {
          modification: null,
          year: null,
          driveType: null,
          additionalOption: null
        },
        isAllSelectsChacked: false,
        imageHovered: false,
        carNameIsChanged: false
      }
    },

    computed: {
      ...mapGetters(['getModel']),
      filtrationResultTitle () {
        return this.modifications.length ? this.subtitle : this.subtitleNotFound
      },
      modelValue () {
        return this.filters.model.value
      },
      carFuelType () {
        return this.modifications.fuelType ? this.modifications.fuelType : ''
      },
      carVolumeType () {
        return this.modifications.volumeType ? this.modifications.volumeType : ''
      },
      carImage () {
        const key = this.currentFilter.year ? 'year' : 'modification'
        if (this.carModification[key]) {
          let imgSrc
          if (this.imageHovered) {
            imgSrc = this.carModification[key].images.hover || this.carModification[key].images.default ||
              this.carModification.modification.images.default || this.car.image
          } else {
            imgSrc = this.carModification[key].images.default || this.carModification.modification.images.default || this.car.image
          }
          return {
            src: imgSrc,
            alt: this.carModification[key].text
          }
        } else {
          return {
            src: this.car.image,
            alt: this.car.name || this.car.year
          }
        }
      },
      carName () {
        return get(this, 'car.name', '')
      }
    },

    watch: {
      isAllSelectsChacked (val) {
        if (val) {
          setTimeout(() => {
            const windowHeight = window.innerHeight
            const pageHeight = document.querySelector('body').offsetHeight
            window.scrollTo({
              top: pageHeight - windowHeight,
              behavior: 'smooth'
            })
          }, 300)
        }
      },
      $route (to, from) {
        if (from.name === 'model' && to.name === 'modification') {
          this.clearFilters()
        }
        if (from.name === 'modification' && to.name === 'model') {
          this.pushDataToDataLayer({
            pages: {
              page: {
                category: 'tools',
                subCategory: 'form',
                sectionlv1: 'forms',
                sectionlv2: 'book-service',
                language: this.$store.state.currentLang
              }
            },
            forms: {
              form: {
                name: 'lead-book-service',
                step: 'select-model',
                conversion: '0'
              }
            }
          })
        }
      },
      carName (newVal, oldVal) {
        if (oldVal) this.carNameIsChanged = true
        if (newVal) this.pushModelToDataLayer(newVal)
      }
    },

    async created () {
      await this.getPageData({ model: this.getModel })

      this.loadDataDebounced = debounce(async () => {
        const { year, driveType, additionalOption, modification } = this.currentFilter

        if (!modification) return

        await this.getPageData({
          model: this.getModel,
          year,
          drive: driveType,
          additional: additionalOption === '-' ? null : additionalOption,
          modification
        })

        await this.$nextTick()
        this.showFilters()
      }, DEBOUNCE_REQUESTS)

      Object.keys(this.currentFilter).forEach(filterKey => {
        this.$watch(`currentFilter.${filterKey}`, (value) => {
          this.setCarInfo(value, filterKey)

          if (filterKey === 'modification') {
            this.currentFilter = {
              modification: value,
              year: null,
              driveType: null,
              additionalOption: null
            }
          }

          this.loadDataDebounced()
        })
      })

      this.componentReady = true
    },

    mounted () {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ 'event': 'formevent' })
    },

    methods: {
      get,
      ...mapActions(['fetchModificationPageData', 'submitModification']),
      getLinkToNextStep (modification) {
        return {
          name: 'tech-map',
          params: {
            modification: modification.value
          }
        }
      },
      async getPageData ({ model, year, drive, additional, modification }) {
        const data = await this.fetchModificationPageData({
          model, year, drive, additional, modification
        })

        this._data = Object.assign(this._data, data)
      },
      clearFilters () {
        this.currentFilter = {
          modification: null,
          year: null,
          driveType: null,
          additionalOption: null
        }
      },
      showFilters () {
        if (this.filters.additionalOption.types.length === 0) {
          const filterWithoutAdditional = { ...this.currentFilter }
          delete filterWithoutAdditional.additionalOption
          this.isAllSelectsChacked = Object.values(filterWithoutAdditional).every(el => el != null)
        } else {
          this.isAllSelectsChacked = Object.values(this.currentFilter).every(el => el != null)
        }
      },
      setCarInfo (filter, key) {
        switch (key) {
        case 'year' :
          this.carModification.year = this.filters.year.types.find(el => el.value === filter)
          break
        case 'driveType' :
          this.carModification.driveType = this.filters.driveType.types.find(el => el.value === filter)
          break
        case 'additionalOption' :
          this.carModification.additionalOption = this.filters.additionalOption.types.find(el => el.value === filter)
          break
        case 'modification' :
          this.carModification.modification = this.filters.modification.types.find(el => el.value === filter)
          break
        default :
          break
        }
      },
      async submitForm () {
        try {
          const res = await this.submitModification({
            form: this.currentFilter
          })
          const modification = res.url.split('=')[1]

          this.$store.commit('SET_MODEL', {
            modelText: this.modelText,
            modelId: this.modelId
          })
          this.pushDataToDataLayer({
            events: {
              event: {
                name: 'formevent',
                componentName: 'book-service',
                componentGroup: 'forms',
                action: 'form-step',
                label: 'select-service'
              }
            },
            pages: {
              page: {
                category: 'tools',
                subCategory: 'form',
                sectionlv1: 'forms',
                sectionlv2: 'book-service',
                language: this.$store.state.currentLang
              }
            },
            forms: {
              form: {
                name: 'lead-book-service',
                step: 'select-service',
                conversion: '0'
              }
            },
            products: {
              product: [{
                category: 'cars',
                subCategory: 'new-cars',
                productInfo: {
                  productId: this.modelId,
                  productName: this.modelText,
                  tmeModelId: this.modelId,
                  modelName: this.modelText
                }
              }]
            }
          }, true)

          let query = {}

          if (this.$store.state.currentLang !== DEFAULT_LANG) {
            query = { lang: this.$store.state.currentLang }
          }

          this.$router.push({
            name: 'tech-map',
            params: { modification },
            query: query
          })
        } catch (err) {
          console.error(err)
        }
      },
      goBack () {
        let query = {}

        if (this.$store.state.currentLang !== DEFAULT_LANG) {
          query = { lang: this.$store.state.currentLang }
        }

        this.$router.push({ name: 'model', query: query })
      },
      onImageEnterLEave (value) {
        this.imageHovered = value
      },

      checkNull (data) {
        if (data) return data
        return ''
      },
      setTextModel (data) {
        this.modelText = data || ''
      },
      setIdModel (data) {
        this.modelId = data ? data.toLowerCase() : ''
      },
      pushModelToDataLayer (data) {
        const modelCorrespondenceTable = [
          { text: 'LC Convertible', id: 'le' },
          { text: 'LC-Convertible', id: 'le' },
          { text: 'LCConvertible', id: 'le' },
          { text: 'UX 300e', id: 'ux-300e' },
          { text: 'UX-300e', id: 'ux-300e' },
          { text: 'UX300e', id: 'ux-300e' }
        ]
        if (!this.carNameIsChanged) {
          this.setTextModel(data)
          this.setIdModel(data)
        } else if (modelCorrespondenceTable.find(item => item.text === data)) {
          this.setTextModel(data)
          this.modelId = this.checkNull(get(modelCorrespondenceTable.find(item => item.text === data), 'id', ''))
        }
        this.pushDataToDataLayer({
          events: {
            event: {
              name: 'formevent',
              componentName: 'book-service',
              componentGroup: 'forms',
              action: 'form-step',
              label: 'select-grade'
            }
          },
          pages: {
            page: {
              category: 'tools',
              subCategory: 'form',
              sectionlv1: 'forms',
              sectionlv2: 'book-service',
              language: this.$store.state.currentLang
            }
          },
          forms: {
            form: {
              name: 'lead-book-service',
              step: 'select-grade',
              conversion: '0'
            }
          },
          products: {
            product: [{
              category: 'cars',
              subCategory: 'new-cars',
              productInfo: {
                productId: this.modelId,
                productName: this.modelText,
                tmeModelId: this.modelId,
                modelName: this.modelText
              }
            }]
          }
        })
      },
      pushDataToDataLayer (params = {}, pushDataLayer = false) {
        window.dataLayer = window.dataLayer || []
        window.tngaDataLayer = { ...params.events, ...params.pages, ...params.forms, ...params.products }
        if (pushDataLayer) window.dataLayer.push({ 'event': 'formevent' })
      }
    }
  }
</script>

<style lang="scss">
.modifications-page {
  &__prev {
    min-width: 81px;
    margin-bottom: 80px;

    @include mobile {
      margin-bottom: 40px;
    }
  }

  h2 {
    margin-bottom: 60px;

    @include tablet {
      margin-bottom: 30px;
    }

    @include mobile {
      margin-bottom: 40px;
    }
  }

  &__content {
    display: flex;
    justify-content: space-between;

    @include tablet {
      flex-direction: column;
    }
  }

  &__filters {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    max-width: 450px;

    @include tablet {
      max-width: 100%;
    }
  }

  &__filter {
    width: 100%;
    max-width: 100%;

    @include mobile {
      width: 100%;
      min-width: 100%;
    }
  }

  &__hr {
    width: 100%;
    height: 1px;
    margin: 5px auto 35px auto;
    background-color: $c-gray-medium;

    @include mobile {
      margin: 15px auto;
    }
  }

  &__modifications {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding-bottom: 50px;
  }

  &__modification {
    display: inline-block;
    width: 335px;
    margin-bottom: 20px;
    font-family: "FavoritC", Arial, Helvetica, sans-serif;

    &:last-child {
      margin-bottom: 0;
    }

    @include mobile {
      width: 100%;
    }
  }

  &__clear_all-wrapper {
    display: flex;
    flex-flow: row-reverse;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-top: 15px;

    @include mobile {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__next {
    @include mobile {
      margin-top: 60px;
    }
  }
}
</style>
